import { useEffect, useState } from 'react'

export type ArticleAudioKind = 'article' | 'state'

export interface ArticleAudioEpisode {
  audioUrl: string
  transcriptUrl?: string
  durationSeconds?: number
  generatedAt?: string
  model?: string
  voice: string
}

export type ArticleAudioStatus =
  | { status: 'checking' }
  | { status: 'preparing' }
  | { status: 'ready'; episode: ArticleAudioEpisode }
  | { status: 'unavailable' }

type AudioPayload = {
  status?: unknown
  audioUrl?: unknown
  transcriptUrl?: unknown
  durationSeconds?: unknown
  generatedAt?: unknown
  model?: unknown
  voice?: unknown
}

const PREPARING_RETRY_MS = 60_000

const optionalText = (value: unknown) => typeof value === 'string' && value.trim() ? value.trim() : undefined

export function articleAudioEndpoint(kind: ArticleAudioKind, id: string): string {
  return `/api/article-audio?${new URLSearchParams({ kind, id }).toString()}`
}

export function articleAudioRequestEndpoint(kind: ArticleAudioKind, id: string, requestId: string): string {
  return `${articleAudioEndpoint(kind, id)}&refresh=${encodeURIComponent(requestId)}`
}

export function resolveArticleAudioResponse(statusCode: number, value: unknown): ArticleAudioStatus {
  if (statusCode === 202) return { status: 'preparing' }
  if (statusCode === 404) return { status: 'unavailable' }
  if (statusCode < 200 || statusCode >= 300 || !value || typeof value !== 'object') return { status: 'unavailable' }

  const payload = value as AudioPayload
  if (payload.status !== 'ready') return { status: 'preparing' }
  const audioUrl = optionalText(payload.audioUrl)
  if (!audioUrl) return { status: 'unavailable' }
  const durationSeconds = typeof payload.durationSeconds === 'number' && Number.isFinite(payload.durationSeconds) && payload.durationSeconds >= 0
    ? payload.durationSeconds
    : undefined

  return {
    status: 'ready',
    episode: {
      audioUrl,
      transcriptUrl: optionalText(payload.transcriptUrl),
      durationSeconds,
      generatedAt: optionalText(payload.generatedAt),
      model: optionalText(payload.model),
      voice: optionalText(payload.voice) ?? 'Sage',
    },
  }
}

export function useArticleAudio(kind: ArticleAudioKind, id: string, enabled = true, refreshKey = 0): ArticleAudioStatus {
  const requestKey = `${kind}:${id}`
  const [result, setResult] = useState<{ key: string; value: ArticleAudioStatus }>({ key: requestKey, value: { status: 'checking' } })

  useEffect(() => {
    if (!enabled || !id) return
    let disposed = false
    let controller: AbortController | null = null
    let retryTimer = 0

    const load = async () => {
      controller?.abort()
      controller = new AbortController()
      try {
        const response = await fetch(articleAudioRequestEndpoint(kind, id, `${Date.now()}-${refreshKey}`), { signal: controller.signal, cache: 'no-store', headers: { Accept: 'application/json' } })
        const payload = response.headers.get('content-type')?.includes('application/json') ? await response.json() as unknown : null
        if (disposed) return
        const next = resolveArticleAudioResponse(response.status, payload)
        setResult({ key: requestKey, value: next })
        if (next.status === 'preparing') retryTimer = window.setTimeout(() => void load(), PREPARING_RETRY_MS)
      } catch (cause) {
        if (!disposed && (cause as Error).name !== 'AbortError') setResult({ key: requestKey, value: { status: 'unavailable' } })
      }
    }

    void load()
    return () => { disposed = true; controller?.abort(); window.clearTimeout(retryTimer) }
  }, [enabled, id, kind, refreshKey, requestKey])

  if (!enabled || !id) return { status: 'unavailable' }
  return result.key === requestKey ? result.value : { status: 'checking' }
}
